import { t } from "@lingui/macro";
import {
  ProfitAndLossFilterOptionsDocument,
  type ProfitAndLossFilterOptionsQuery,
  type ProfitAndLossFilterOptionsQueryVariables,
  ProfitAndLossReportDocument,
  type ProfitAndLossReportQuery,
  type ProfitAndLossReportQueryVariables,
  ProfitAndLossReportSummaryDocument,
  type ProfitAndLossReportSummaryQuery,
  type ProfitAndLossReportSummaryQueryVariables,
  type ProfitLossReport,
  type ProfitLossReportColumn,
  ProfitLossReportColumns,
  ProfitLossReportRows,
  ProfitLossReportWhereColumn,
  type ProfitLossReportWhereConditions,
  ProjectStatusEnum,
  SqlOperator,
} from "@src/__generated__/graphql";
import { getBillingCategoryOptions } from "@src/components/modules/projects/list/Store";
import { buildProfitAndLossColumns } from "@src/components/modules/reports/profit-and-loss/builders";
import type { BodyColumnDef, IOption } from "@src/components/ui-kit";
import { ProjectCategorySelectBottomExtraContent } from "@src/components/ui-kit/ProjectCategorySelect";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import type { AppStore } from "@src/stores/AppStore";
import { getBooleanFilterOptions } from "@src/utils/components/filters/constants";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { billingEntityOptionsQuery } from "@src/utils/components/filters/paginatedOptions";
import { toApiDate } from "@src/utils/dates";
import { currency } from "@src/utils/formatters";
import mapToOptions from "@src/utils/map-to-options";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import { getQueryParamOptional } from "@src/utils/routing";
import type { DateRange } from "@src/utils/types";
import { saveQuery } from "@src/utils/url";
import { endOfYear, startOfYear } from "date-fns";
import { upperFirst } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import Router from "next/router";

interface TableDataRow extends ProfitLossReport {
  id: string;
  total: Omit<ProfitLossReportColumn, "title">;
}

type ProfitAndLossGroupRow = Record<string, unknown> & {
  id: string;
  rowType: "group";
  row: TableDataRow;
  __subRows: ProfitAndLossTableRow[];
};

type ProfitAndLossItemRow = Record<string, unknown> & {
  id: string;
  rowType: "item";
  row: TableDataRow;
};

export type ProfitAndLossTableRow =
  | ProfitAndLossGroupRow
  | ProfitAndLossItemRow;

type TableSummary = {
  columns: ProfitLossReportColumn[];
  total: Omit<ProfitLossReportColumn, "title"> | undefined;
};

export type ProfitAndLossBodyColumn = BodyColumnDef<ProfitAndLossTableRow>;

export class ProfitAndLossReportStore {
  readonly moduleKey = "profit-and-loss";

  pagination = new PaginationState(this.moduleKey, {
    onChangePagination: (pagination) => this.handlePaginationChange(pagination),
  });

  where = new Filters(
    [
      new Filter({
        title: t`Legal entity`,
        column: ProfitLossReportWhereColumn.WorkspaceCompanyId,
        operator: SqlOperator.In,
        options: [],
        value: [],
      }),
      new Filter({
        column: ProfitLossReportWhereColumn.BrandId,
        operator: SqlOperator.In,
        title: t`Client/Brand`,
        tagTitle: t`Brand`,
        sectioned: true,
        hasSelectAllOption: true,
        options: [],
        value: [],
      }),
      new Filter({
        title: t`Billing entity`,
        column: ProfitLossReportWhereColumn.BillingEntityId,
        operator: SqlOperator.In,
        isPaginated: true,
        perPage: 10,
        optionsQuery: billingEntityOptionsQuery,
        options: [],
        value: [],
      }),
      new Filter({
        title: t`Team`,
        column: ProfitLossReportWhereColumn.TeamId,
        operator: SqlOperator.In,
        options: [],
        value: [],
      }),
      new Filter({
        title: t`Project`,
        column: ProfitLossReportWhereColumn.ProjectId,
        operator: SqlOperator.In,
        options: [],
        value: [],
      }),
      new Filter({
        title: t`Project manager`,
        column: ProfitLossReportWhereColumn.ProjectManagerId,
        operator: SqlOperator.In,
        options: [],
        value: [],
      }),
      new Filter({
        title: t`Other project manager`,
        column: ProfitLossReportWhereColumn.OtherProjectManagerId,
        operator: SqlOperator.In,
        options: [],
        value: [],
      }),
      new Filter({
        title: t`Project billing category`,
        column: ProfitLossReportWhereColumn.ProjectBillable,
        operator: SqlOperator.Eq,
        options: getBillingCategoryOptions(),
        value: [],
      }),
      new Filter({
        title: t`Project category`,
        column: ProfitLossReportWhereColumn.ProjectCategoryId,
        operator: SqlOperator.In,
        options: [],
        value: [],
        bottomExtraContent: <ProjectCategorySelectBottomExtraContent />,
      }),
      new Filter({
        title: t`Project status`,
        column: ProfitLossReportWhereColumn.ProjectStatus,
        operator: SqlOperator.In,
        options: [
          {
            value: ProjectStatusEnum.Active,
            label: t`Active`,
          },
          {
            value: ProjectStatusEnum.Finished,
            label: t`Finished`,
          },
          {
            value: ProjectStatusEnum.Archived,
            label: t`Archived`,
          },
        ],
        value: [],
      }),
      new Filter({
        title: t`Include draft expenses`,
        column: "include_draft_expenses",
        operator: SqlOperator.Eq,
        options: getBooleanFilterOptions(),
        includeInGql: false,
      }),
    ],
    {
      onChange: () => {
        this.pagination.resetPage();
        this.fetchReportSummary();
      },
      tableKey: this.moduleKey,
    },
  );

  private fetcher = new FetchHelper<
    ProfitAndLossReportQuery,
    ProfitAndLossReportQueryVariables
  >(ProfitAndLossReportDocument);

  private fetcherSummary = new FetchHelper<
    ProfitAndLossReportSummaryQuery,
    ProfitAndLossReportSummaryQueryVariables
  >(ProfitAndLossReportSummaryDocument);

  private fetcherFilterOptions = new FetchHelper<
    ProfitAndLossFilterOptionsQuery,
    ProfitAndLossFilterOptionsQueryVariables
  >(ProfitAndLossFilterOptionsDocument);

  @observable.ref
  rows = ProfitLossReportRows.Project;

  @observable.ref
  columns: ProfitLossReportColumns | undefined = ProfitLossReportColumns.Month;

  @observable.ref
  dateRange: DateRange = {
    start: startOfYear(new Date()),
    end: endOfYear(new Date()),
  };

  @observable.ref
  tableSummary: TableSummary = {
    columns: [],
    total: undefined,
  };

  @observable.ref
  tableData: ProfitAndLossTableRow[] = [];

  @observable.ref
  tableColumns: ProfitAndLossBodyColumn[] = [];

  constructor(private appStore: AppStore) {
    makeObservable(this);
    this.fetchFilterOptions();

    const from = getQueryParamOptional("from", Router.query);
    const to = getQueryParamOptional("to", Router.query);
    const rows = getQueryParamOptional("rows", Router.query);
    const columns = getQueryParamOptional("columns", Router.query);

    if (from && to) {
      this.dateRange = {
        start: new Date(from),
        end: new Date(to),
      };
    }

    if (rows) {
      this.rows = rows as ProfitLossReportRows;
    }

    if (columns) {
      this.columns = columns as ProfitLossReportColumns;
    }

    this.where.fromURLSearchParam(Router.query);
    this.pagination.fromURLSearchParam(Router.query);

    this.fetchReportSummary();
  }

  @computed
  get isLoading(): boolean {
    return this.fetcher.isLoading.value;
  }

  @computed
  get isLoadingSummary(): boolean {
    return this.fetcherSummary.isLoading.value;
  }

  @computed
  get rowsOptions(): IOption[] {
    return Object.entries(ProfitLossReportRows).map(([, value]) => ({
      value,
      label: upperFirst(value.toLowerCase()),
    }));
  }

  @computed
  get columnsOptions(): IOption[] {
    return Object.entries(ProfitLossReportColumns).map(([, value]) => ({
      value,
      label: upperFirst(value.toLowerCase()),
    }));
  }

  @action.bound
  handleChangeRows(rows: ProfitLossReportRows): void {
    this.rows = rows;
    this.pagination.resetPage();
  }

  @action.bound
  handleChangeColumns(columns: ProfitLossReportColumns | undefined): void {
    this.columns = columns;
    this.pagination.resetPage();
    this.fetchReportSummary();
  }

  @action.bound
  handleChangeDateRange(dateRange: DateRange): void {
    this.dateRange = dateRange;
    this.pagination.resetPage();
    this.fetchReportSummary();
  }

  @action.bound
  private handlePaginationChange(pagination: PaginationState): void {
    const query = {
      page: pagination.asURLSearchParam.page,
      first: pagination.asURLSearchParam.first,
      where: this.where.asURLSearchParam.where,
      from: toApiDate(this.dateRange.start),
      to: toApiDate(this.dateRange.end),
      rows: this.rows,
      columns: this.columns,
    };

    Router.push(
      {
        query,
      },
      undefined,
      {
        shallow: true,
      },
    );
    saveQuery(query);
    this.fetchReport(pagination);
  }

  @computed
  get includeDraftExpenses(): boolean | undefined {
    const filterValue = this.where.filtersByColumn.get(
      "include_draft_expenses",
    )?.value;
    return filterValue?.length ? filterValue[0] === "true" : undefined;
  }

  @action.bound
  async fetchReportSummary(): Promise<void> {
    const [data, error] = await this.fetcherSummary.fetch({
      // NOTE: We can safely typecast here, because the extra column type is excluded from query
      where: this.where.asGraphQueryParam as ProfitLossReportWhereConditions,
      from: toApiDate(this.dateRange.start),
      to: toApiDate(this.dateRange.end),
      columns: this.columns,
      includeDraftExpenses: this.includeDraftExpenses,
    });

    if (error) return;

    this.tableSummary = {
      columns: data.profitLossReportSummary.data,
      total: this.getColumnsTotals(data.profitLossReportSummary.data),
    };
  }

  @action.bound
  async fetchReport(pagination: PaginationState): Promise<void> {
    const [data, error] = await this.fetcher.fetch({
      // NOTE: We can safely typecast here, because the extra column type is excluded from query
      where: this.where.asGraphQueryParam as ProfitLossReportWhereConditions,
      from: toApiDate(this.dateRange.start),
      to: toApiDate(this.dateRange.end),
      rows: this.rows,
      columns: this.columns,
      page: pagination.asParams.page,
      first: pagination.asParams.first,
      includeDraftExpenses: this.includeDraftExpenses,
    });

    if (error) return;

    const columnTitles: string[] = [];
    const tableData: ProfitAndLossTableRow[] = [];

    for (const group of data.profitLossReport.data) {
      for (const column of group.columns) {
        if (columnTitles.includes(column.title)) continue;
        columnTitles.push(column.title);
      }

      for (const item of group.items) {
        for (const column of item.columns) {
          if (columnTitles.includes(column.title)) continue;
          columnTitles.push(column.title);
        }
      }

      const groupRow: TableDataRow = {
        id: group.id,
        title: group.title,
        columns: group.columns,
        total: this.getColumnsTotals(group.columns),
      };

      tableData.push({
        id: group.id,
        rowType: "group",
        row: groupRow,
        __subRows: group.items.map((item) => ({
          id: item.id,
          rowType: "item",
          row: {
            id: item.id,
            title: item.title,
            columns: item.columns,
            total: this.getColumnsTotals(item.columns),
          },
        })),
      });
    }

    this.tableData = tableData;
    columnTitles.sort();
    this.tableColumns = buildProfitAndLossColumns(
      columnTitles,
      this.rows,
      this.columns,
      this.dateRange,
    );
    this.pagination.setFromPaginatorInfo(data.profitLossReport.paginatorInfo);
  }

  @action.bound
  async fetchFilterOptions(): Promise<void> {
    const [data, error] = await this.fetcherFilterOptions.fetch();

    if (error) return;

    this.where.filtersByColumn
      .get(ProfitLossReportWhereColumn.BrandId)
      ?.setOptions(mapToOptions.brandsSectioned(data.clientsSimpleMap));
    this.where.filtersByColumn
      .get(ProfitLossReportWhereColumn.ProjectId)
      ?.setOptions(mapToOptions.projects(data.projectsSimpleMap));
    this.where.filtersByColumn
      .get(ProfitLossReportWhereColumn.ProjectManagerId)
      ?.setOptions(
        mapToOptions.projectManagers.toOptions(data.projectManagerSimpleMap),
      );
    this.where.filtersByColumn
      .get(ProfitLossReportWhereColumn.OtherProjectManagerId)
      ?.setOptions(
        mapToOptions.projectManagers.toOptions(data.projectManagerSimpleMap),
      );
    this.where.filtersByColumn
      .get(ProfitLossReportWhereColumn.ProjectCategoryId)
      ?.setOptions(
        data.projectCategories.map(({ id, name }) => ({
          value: id,
          label: name,
        })),
      );
    this.where.filtersByColumn
      .get(ProfitLossReportWhereColumn.TeamId)
      ?.setOptions(mapToOptions.teams(data.teamSimpleMap));
    this.where.filtersByColumn
      .get(ProfitLossReportWhereColumn.WorkspaceCompanyId)
      ?.setOptions(
        mapToOptions.workspaceCompanies(this.appStore.workspaceStore.companies),
      );
  }

  graphLegendFormatter(name: string): string {
    return upperFirst(name.toLowerCase().replace("_", " "));
  }

  @action.bound
  graphTooltipFormatter(value: string, name: string): [string, string] {
    return [currency.formatByWorkspace(value), this.graphLegendFormatter(name)];
  }

  getColumnsTotals(
    columns: ProfitLossReportColumn[],
  ): Omit<ProfitLossReportColumn, "title"> {
    return columns.reduce(
      (acc, { invoiced_amount, expenses_amount, balance_amount }) => ({
        invoiced_amount: acc.invoiced_amount + invoiced_amount,
        expenses_amount: acc.expenses_amount + expenses_amount,
        balance_amount: acc.balance_amount + balance_amount,
      }),
      {
        invoiced_amount: 0,
        expenses_amount: 0,
        balance_amount: 0,
      },
    );
  }
}
